import { ResourceType } from '@cotera/api';
import { resourceKind } from '@cotera/sdk/core';
import { isArtifactViewable } from '@cotera/client/v0/chat/artifacts';

/** A workspace resource to show, plus everything either surface might need. */
export type ResourceTarget = {
  resourceType: ResourceType;
  resourceId: string;
  /** Display name, used by artifact payloads that carry one (folders). */
  name?: string;
  /**
   * Extra artifact payload keys, merged over the type's default payload —
   * e.g. `{ isNew: true }` for a resource that doesn't exist server-side yet.
   */
  payload?: Record<string, unknown>;
  /** Router state. Only used when this falls through to a page navigation. */
  state?: unknown;
};

export type NavigateOptions = { state?: unknown; replace?: boolean };

/**
 * Where a resource should be shown. Implementations differ by surface: a
 * top-level page navigates, an artifact panel opens a tab beside what the user
 * is already looking at.
 *
 * Actions resolve this at execute time rather than holding a `navigate`, so the
 * same action does the right thing from the datasets list, the ⌘K palette inside
 * a chat, and a folder rendered as an artifact.
 */
export type ResourceNavigator = {
  /** Show a workspace resource on the best surface for the current context. */
  open(target: ResourceTarget): void;
  /** Escape hatch for destinations that aren't a resource (list pages, settings). */
  navigate(path: string, options?: NavigateOptions): void;
};

/** Route to a resource's full page. Every type has one. */
export const resourceItemPath = (
  orgSlug: string,
  resourceType: ResourceType,
  resourceId: string
): string => {
  const { itemRouteSegment } = resourceKind(resourceType);
  return `/${orgSlug}/${itemRouteSegment}/${resourceId}`;
};

/** Navigates the whole app to the resource's page. The default surface. */
export class PageResourceNavigator implements ResourceNavigator {
  constructor(
    private readonly navigateFn: (
      path: string,
      options?: NavigateOptions
    ) => void,
    private readonly orgSlug: string
  ) {}

  open(target: ResourceTarget): void {
    const path = resourceItemPath(
      this.orgSlug,
      target.resourceType,
      target.resourceId
    );
    if (path === null) {
      return;
    }
    this.navigate(
      path,
      target.state === undefined ? undefined : { state: target.state }
    );
  }

  navigate(path: string, options?: NavigateOptions): void {
    this.navigateFn(path, options);
  }
}

/** How an artifact panel is asked to show a resource. */
export type ArtifactOpener = (input: {
  resourceType: ResourceType;
  resourceId: string;
  name?: string;
  payload: Record<string, unknown>;
}) => void;

/**
 * Opens resources as artifacts in whichever panel is hosting the caller, falling
 * back to `fallback` for types the panel can't render.
 *
 * The opener is injected rather than baked in so the chat panel and the editor
 * page panel share this. Whether an open is also recorded on a conversation is
 * the panel's business, not the navigator's — `ChatWithArtifactPanel` registers
 * resource opens on the chat, the standalone editor panel keeps them local.
 */
export class ArtifactPanelResourceNavigator implements ResourceNavigator {
  constructor(
    private readonly opts: {
      openArtifact: ArtifactOpener;
      /** Defaults to {@link isArtifactViewable}. */
      canOpenAsArtifact?: (resourceType: ResourceType) => boolean;
      fallback: ResourceNavigator;
      /** Builds the panel payload for a target. */
      buildPayload: (target: ResourceTarget) => Record<string, unknown>;
    }
  ) {}

  open(target: ResourceTarget): void {
    const canOpen = this.opts.canOpenAsArtifact ?? isArtifactViewable;
    if (!canOpen(target.resourceType)) {
      this.opts.fallback.open(target);
      return;
    }
    this.opts.openArtifact({
      resourceType: target.resourceType,
      resourceId: target.resourceId,
      ...(target.name !== undefined && { name: target.name }),
      payload: this.opts.buildPayload(target),
    });
  }

  navigate(path: string, options?: NavigateOptions): void {
    this.opts.fallback.navigate(path, options);
  }
}
